/**
 * Scam Defense Simulator — scenario pool.
 *
 * Each scenario is an interactive scene the learner must inspect.
 * Hotspots are clickable spans the learner must identify before acting,
 * actions trigger consequences against the learner's existing HeirWise
 * accounts (no fake balances), and `bestPath` defines the action the
 * scoring engine treats as the strongest defense.
 *
 * No personalized financial/legal advice. No trait labeling. Concepts are
 * display-only chips drawn from FROZEN_DICTIONARY_CONCEPTS in the module.
 */

export type ScenarioType =
  | "email"
  | "text"
  | "marketplace"
  | "social_dm"
  | "payment_app"
  | "browser"
  | "phone"
  | "qr"
  | "invoice";

export type AccountTarget = "checking" | "savings" | "spending";

export type ActionId =
  | "ignore"
  | "close_tab"
  | "click_link"
  | "open_attachment"
  | "enter_password"
  | "enter_otp"
  | "pay_fee"
  | "send_money"
  | "share_card"
  | "reply"
  | "verify_in_official_app"
  | "call_official_number"
  | "search_company"
  | "ask_parent"
  | "freeze_card"
  | "report_phishing"
  | "block_sender"
  | "keep_payment_in_platform"
  | "refuse_off_platform"
  | "cancel_transaction";

export interface Hotspot {
  id: string;
  /** A phrase from the rendered content the learner must click. Matched verbatim. */
  phrase: string;
  /** Where the phrase lives in the rendered scene (body | sender | subject | url | meta). */
  region: "sender" | "subject" | "body" | "url" | "meta";
  label: string;
  explanation: string;
  severity: "low" | "medium" | "high";
  /** Display-only concept chip key (frozen dictionary). */
  concept?: string;
}

export interface Consequence {
  /** Negative = simulated debit, positive = avoided loss / neutral. */
  moneyDelta: number;
  account?: AccountTarget;
  identityRiskDelta: number;
  composureDelta: number;
  /** True if this action triggers the recovery checklist. */
  triggersRecovery?: boolean;
  message: string;
}

export interface ActionOption {
  id: ActionId;
  label: string;
  tone: "danger" | "neutral" | "safe";
  description?: string;
}

export interface ScamScenario {
  id: string;
  type: ScenarioType;
  source: string; // inbox / texts / marketplace / browser etc — left-panel label
  title: string;
  startingContext: string;
  /** Free-form scene content. The module renders the relevant fields by type. */
  scene: {
    from?: string;
    subject?: string;
    body: string;
    displayedUrl?: string;
    timestamp?: string;
    attachment?: string;
    amount?: number;
    productName?: string;
    rating?: string;
  };
  hotspots: Hotspot[];
  actions: ActionOption[];
  consequences: Partial<Record<ActionId, Consequence>>;
  bestAction: ActionId;
  conceptKeys: string[];
  explainer: string;
}

export const SCAM_SCENARIOS: ScamScenario[] = [
  // 1. Fake Bank Login Alert
  {
    id: "fake-bank-login-alert",
    type: "email",
    source: "Inbox",
    title: "Unusual sign-in detected",
    startingContext: "An email lands in your inbox claiming someone signed in to your bank account.",
    scene: {
      from: "security@bank-alerts-support.com",
      subject: "URGENT: unusual sign-in detected — confirm in 15 minutes",
      body: "We blocked a sign-in from a new device. Confirm your account in the next 15 minutes by entering your password at the link below or your account will be locked.",
      displayedUrl: "bank-alerts-support.com/login",
      timestamp: "2 min ago",
    },
    hotspots: [
      { id: "sender", phrase: "security@bank-alerts-support.com", region: "sender", label: "Suspicious sender domain", explanation: "Real banks email from their own primary domain, not a 'support' lookalike.", severity: "high", concept: "verification_habit" },
      { id: "urgency", phrase: "next 15 minutes", region: "body", label: "Urgency manipulation", explanation: "A 15-minute deadline pressures you to skip verification.", severity: "medium", concept: "urgency_manipulation" },
      { id: "creds", phrase: "entering your password", region: "body", label: "Credential request", explanation: "No legitimate bank asks you to type your password via an email link.", severity: "high" },
      { id: "url", phrase: "bank-alerts-support.com/login", region: "url", label: "URL mismatch", explanation: "This is not the bank's real login domain.", severity: "high", concept: "verification_habit" },
    ],
    actions: [
      { id: "click_link", label: "Click the link", tone: "danger" },
      { id: "enter_password", label: "Enter password on the page", tone: "danger" },
      { id: "verify_in_official_app", label: "Open the official bank app and check", tone: "safe" },
      { id: "report_phishing", label: "Report phishing", tone: "neutral" },
      { id: "ignore", label: "Ignore for now", tone: "neutral" },
    ],
    consequences: {
      click_link: { moneyDelta: 0, identityRiskDelta: 15, composureDelta: -5, message: "You opened a fake login page. No money was lost yet, but you exposed yourself to credential capture." },
      enter_password: { moneyDelta: -300, account: "checking", identityRiskDelta: 60, composureDelta: -25, triggersRecovery: true, message: "Your password was captured. The attacker drained $300 from your checking account before the freeze caught up." },
      verify_in_official_app: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 5, message: "You verified safely. No alert in the real app — confirmed phishing." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: -2, composureDelta: 2, message: "Reported. The provider can now warn other users." },
      ignore: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 0, message: "Ignoring is safer than clicking, but reporting helps protect others." },
    },
    bestAction: "verify_in_official_app",
    conceptKeys: ["urgency_manipulation", "verification_habit"],
    explainer: "Identify the sender domain, urgency cue, credential ask, and URL mismatch before acting. The strongest defense is to open the bank app directly — never through the email link.",
  },

  // 2. Fake Delivery Fee Text
  {
    id: "fake-delivery-fee",
    type: "text",
    source: "Texts",
    title: "Package on hold — $3.95 redelivery fee",
    startingContext: "A text message arrives about a package.",
    scene: {
      from: "+1 (244) 555-0119",
      body: "USPS: your package is on hold. Pay the $3.95 redelivery fee within 24 hours or it will be returned. Update your address: bit.ly/usps-redeliv-2k9",
      displayedUrl: "bit.ly/usps-redeliv-2k9",
      timestamp: "Just now",
    },
    hotspots: [
      { id: "shortlink", phrase: "bit.ly/usps-redeliv-2k9", region: "url", label: "Shortened suspicious link", explanation: "Carriers don't send shortened bit.ly links — it hides the real destination.", severity: "high" },
      { id: "urgency", phrase: "within 24 hours", region: "body", label: "Urgency pressure", explanation: "Pressure to act now is a hallmark of fake-delivery scams.", severity: "medium", concept: "urgency_manipulation" },
      { id: "no-order", phrase: "your package is on hold", region: "body", label: "No order number", explanation: "Real carriers reference your tracking number, not a generic 'your package'.", severity: "medium" },
      { id: "fee", phrase: "$3.95 redelivery fee", region: "body", label: "Small fee bait", explanation: "A tiny fee gets your card number — the real loss is the follow-up charge.", severity: "high" },
    ],
    actions: [
      { id: "click_link", label: "Open the link", tone: "danger" },
      { id: "pay_fee", label: "Pay the $3.95 fee", tone: "danger" },
      { id: "verify_in_official_app", label: "Check the carrier's official app/site", tone: "safe" },
      { id: "report_phishing", label: "Report as spam", tone: "neutral" },
      { id: "block_sender", label: "Block sender", tone: "neutral" },
    ],
    consequences: {
      click_link: { moneyDelta: 0, identityRiskDelta: 10, composureDelta: -3, message: "You opened a smishing page. No money lost, but the link tried to load a card-capture form." },
      pay_fee: { moneyDelta: -45.95, account: "spending", identityRiskDelta: 30, composureDelta: -10, triggersRecovery: true, message: "You paid the $3.95 fee. A second unauthorized $42.00 charge followed within hours." },
      verify_in_official_app: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "No package on hold in the official tracker. Confirmed scam." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: -2, composureDelta: 2, message: "Reported. Carrier flags help shut the campaign down." },
      block_sender: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 1, message: "Blocked. Safe move." },
    },
    bestAction: "verify_in_official_app",
    conceptKeys: ["urgency_manipulation", "verification_habit"],
    explainer: "Tiny 'redelivery fees' are bait to capture card details. Verify orders directly in the carrier's app, never via a shortened text link.",
  },

  // 3. Marketplace Deposit Scam
  {
    id: "marketplace-deposit",
    type: "marketplace",
    source: "Marketplace",
    title: "Buyer wants to send a Zelle deposit",
    startingContext: "A buyer messages about your $150 marketplace listing.",
    scene: {
      from: "@quickbuyer_88",
      body: "Hey, I really want this — can we move off the app? Send me your Zelle and I'll deposit $75 now to hold it. I'll pay the rest when my friend picks it up.",
      timestamp: "5 min ago",
    },
    hotspots: [
      { id: "off-platform", phrase: "move off the app", region: "body", label: "Off-platform pressure", explanation: "Once you leave the marketplace, buyer protection is gone.", severity: "high" },
      { id: "deposit", phrase: "deposit $75 now to hold it", region: "body", label: "Upfront deposit twist", explanation: "Real buyers don't demand deposits to a seller — this pattern reverses into a fake overpayment scam.", severity: "high" },
      { id: "zelle", phrase: "Zelle", region: "body", label: "Unprotected payment method", explanation: "Zelle has no buyer/seller fraud protection.", severity: "medium" },
    ],
    actions: [
      { id: "send_money", label: "Send your Zelle handle", tone: "danger" },
      { id: "refuse_off_platform", label: "Refuse — keep it on the marketplace", tone: "safe" },
      { id: "keep_payment_in_platform", label: "Insist they pay through the platform", tone: "safe" },
      { id: "report_phishing", label: "Report buyer", tone: "neutral" },
    ],
    consequences: {
      send_money: { moneyDelta: -75, account: "spending", identityRiskDelta: 5, composureDelta: -8, message: "You sent your handle and accepted the off-platform deposit. The buyer reversed the payment via fake screenshot — you lost the item and $75 in shipping." },
      refuse_off_platform: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 3, message: "You kept the transaction on-platform. Buyer ghosted — clean save." },
      keep_payment_in_platform: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 3, message: "Smart. The platform's escrow protects both sides." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Reported. The marketplace can investigate the pattern." },
    },
    bestAction: "refuse_off_platform",
    conceptKeys: ["verification_habit"],
    explainer: "Moving off-platform removes buyer/seller protection. Stay inside the marketplace's payment flow even when it's slower.",
  },

  // 4. Fake Scholarship Application
  {
    id: "fake-scholarship",
    type: "email",
    source: "Inbox",
    title: "You've been selected — Future Leaders Scholarship",
    startingContext: "An email arrives congratulating you on a scholarship you don't remember applying for.",
    scene: {
      from: "awards@futureleaders-grant.org",
      subject: "Congratulations! $5,000 scholarship pre-approved",
      body: "You've been pre-approved for a $5,000 Future Leaders Scholarship. To release funds we need a $45 processing fee, your full date of birth, and a copy of your student ID. Funds wire within 48 hours.",
      displayedUrl: "futureleaders-grant.org/release",
    },
    hotspots: [
      { id: "tgttbt", phrase: "pre-approved for a $5,000", region: "body", label: "Too good to be true", explanation: "Legit scholarships don't 'pre-approve' you without an application.", severity: "high", concept: "too_good_to_be_true" },
      { id: "fee", phrase: "$45 processing fee", region: "body", label: "Upfront fee", explanation: "Real scholarships never charge a release fee.", severity: "high" },
      { id: "pii", phrase: "full date of birth", region: "body", label: "Sensitive info request", explanation: "DOB + student ID is enough to start identity theft.", severity: "high" },
    ],
    actions: [
      { id: "pay_fee", label: "Pay the $45 fee", tone: "danger" },
      { id: "reply", label: "Reply with your details", tone: "danger" },
      { id: "search_company", label: "Search the scholarship name independently", tone: "safe" },
      { id: "ask_parent", label: "Ask a parent or counselor", tone: "safe" },
      { id: "report_phishing", label: "Report as phishing", tone: "neutral" },
    ],
    consequences: {
      pay_fee: { moneyDelta: -45, account: "spending", identityRiskDelta: 20, composureDelta: -10, message: "You paid the fee. No scholarship arrived. Card now flagged for fraud watch." },
      reply: { moneyDelta: 0, identityRiskDelta: 45, composureDelta: -10, triggersRecovery: true, message: "You shared DOB and student ID. Identity risk spiked." },
      search_company: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "No record of the program anywhere reputable. Confirmed scam." },
      ask_parent: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 5, message: "Your parent spotted the pattern instantly. Safe outcome." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Reported. Other students protected." },
    },
    bestAction: "ask_parent",
    conceptKeys: ["too_good_to_be_true", "verification_habit"],
    explainer: "Real scholarships do not charge release fees or ask for full DOB by email. Verify through the school or scholarship's official site, and loop in a trusted adult.",
  },

  // 5. Fake Job Offer
  {
    id: "fake-job-equipment",
    type: "email",
    source: "Inbox",
    title: "You're hired — send $180 for equipment",
    startingContext: "A 'job offer' lands without you ever interviewing.",
    scene: {
      from: "hr@global-remote-careers.co",
      subject: "Welcome aboard — $4,200/week role confirmed",
      body: "Congratulations! You're hired with no interview required. To activate your role, send $180 today for the starter equipment kit. We'll reimburse it in your first paycheck. Reply with your bank login so payroll can set up direct deposit.",
    },
    hotspots: [
      { id: "no-interview", phrase: "no interview required", region: "body", label: "Unrealistic offer", explanation: "Real employers always interview.", severity: "high", concept: "too_good_to_be_true" },
      { id: "pay-upfront", phrase: "send $180 today", region: "body", label: "Upfront fee", explanation: "Legit jobs never charge you to start.", severity: "high" },
      { id: "bank-login", phrase: "your bank login", region: "body", label: "Credential request", explanation: "Payroll needs routing/account numbers — never a password.", severity: "high" },
    ],
    actions: [
      { id: "pay_fee", label: "Send the $180 equipment fee", tone: "danger" },
      { id: "reply", label: "Reply with bank login", tone: "danger" },
      { id: "search_company", label: "Search the company independently", tone: "safe" },
      { id: "ask_parent", label: "Ask a parent or mentor", tone: "safe" },
      { id: "ignore", label: "Ignore and delete", tone: "neutral" },
    ],
    consequences: {
      pay_fee: { moneyDelta: -180, account: "savings", identityRiskDelta: 10, composureDelta: -12, message: "You paid the 'equipment fee'. The company vanished. No job, no kit." },
      reply: { moneyDelta: -120, account: "checking", identityRiskDelta: 70, composureDelta: -20, triggersRecovery: true, message: "You shared your bank login. The attacker initiated transfers before the bank locked the account." },
      search_company: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "No legitimate web presence. Confirmed scam." },
      ask_parent: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 5, message: "Your mentor flagged the pattern immediately." },
      ignore: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 1, message: "Deleted — safe." },
    },
    bestAction: "search_company",
    conceptKeys: ["too_good_to_be_true", "verification_habit"],
    explainer: "Real jobs interview you and never charge equipment fees up front. Search the company before responding, and never share a bank login.",
  },

  // 6. Fake Crypto Pitch
  {
    id: "fake-crypto-pitch",
    type: "social_dm",
    source: "Social DMs",
    title: "Guaranteed 32% weekly returns",
    startingContext: "A DM from someone you don't really know pitches an investment.",
    scene: {
      from: "@crypto_mentor_pro",
      body: "I made my last group 32% last week — guaranteed. Send $250 in USDC to my wallet by tonight. Spots close at midnight. Zero risk, I personally cover any losses.",
      timestamp: "Just now",
    },
    hotspots: [
      { id: "guaranteed", phrase: "guaranteed", region: "body", label: "Unrealistic return claim", explanation: "No legitimate investment guarantees weekly returns.", severity: "high", concept: "too_good_to_be_true" },
      { id: "urgency", phrase: "Spots close at midnight", region: "body", label: "Urgency pressure", explanation: "Manufactured scarcity forces fast, emotional decisions.", severity: "medium", concept: "urgency_manipulation" },
      { id: "wallet", phrase: "USDC to my wallet", region: "body", label: "Crypto transfer trap", explanation: "Wallet transfers are irreversible — gone the second they leave.", severity: "high" },
    ],
    actions: [
      { id: "send_money", label: "Send the $250", tone: "danger" },
      { id: "ask_parent", label: "Ask a trusted adult first", tone: "safe" },
      { id: "search_company", label: "Search the handle for complaints", tone: "safe" },
      { id: "report_phishing", label: "Report and block", tone: "neutral" },
    ],
    consequences: {
      send_money: { moneyDelta: -250, account: "savings", identityRiskDelta: 5, composureDelta: -15, message: "You transferred $250 USDC. Wallet went silent within an hour. Funds unrecoverable." },
      ask_parent: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 5, message: "Your adult flagged the unrealistic claim. Safe call." },
      search_company: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "Multiple scam reports on the handle. Confirmed." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Reported. Account flagged." },
    },
    bestAction: "ask_parent",
    conceptKeys: ["too_good_to_be_true", "urgency_manipulation"],
    explainer: "Guarantees + urgency + crypto = scam pattern. Pause, verify, and bring a trusted adult into any 'investment' that finds you.",
  },

  // 7. Fake Subscription Invoice
  {
    id: "fake-subscription-invoice",
    type: "invoice",
    source: "Inbox",
    title: "Annual renewal — $429.99 charged",
    startingContext: "An invoice appears for a service you don't recognize.",
    scene: {
      from: "billing@nortn-secure-renewals.com",
      subject: "Your subscription renewed — invoice #4421",
      body: "Your antivirus subscription has been renewed for $429.99. If you didn't authorize this, call our support line immediately at +1 (800) 555-0177. Open the attached invoice for a refund form.",
      attachment: "Invoice_4421.pdf",
    },
    hotspots: [
      { id: "sender", phrase: "billing@nortn-secure-renewals.com", region: "sender", label: "Suspicious sender", explanation: "Misspelled brand domain ('nortn') is a typosquat.", severity: "high" },
      { id: "panic", phrase: "$429.99", region: "body", label: "Panic-inducing charge", explanation: "Designed to make you call the fake number before thinking.", severity: "medium", concept: "urgency_manipulation" },
      { id: "phone", phrase: "+1 (800) 555-0177", region: "body", label: "Fake support number", explanation: "The 'refund' agent will ask for remote access or card details.", severity: "high" },
      { id: "attachment", phrase: "Invoice_4421.pdf", region: "meta", label: "Suspicious attachment", explanation: "Refund 'forms' often hide credential harvesters or malware.", severity: "high" },
    ],
    actions: [
      { id: "open_attachment", label: "Open the invoice", tone: "danger" },
      { id: "call_official_number", label: "Call the listed number", tone: "danger" },
      { id: "verify_in_official_app", label: "Check your real subscription account", tone: "safe" },
      { id: "report_phishing", label: "Report phishing", tone: "neutral" },
    ],
    consequences: {
      open_attachment: { moneyDelta: 0, identityRiskDelta: 25, composureDelta: -8, message: "The attachment tried to load a credential capture form." },
      call_official_number: { moneyDelta: -99, account: "spending", identityRiskDelta: 40, composureDelta: -15, triggersRecovery: true, message: "The 'agent' walked you through installing remote-access software and charged a $99 'repair fee'." },
      verify_in_official_app: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "No charge in your real account. Confirmed fake invoice." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Reported. Provider can flag the typosquat domain." },
    },
    bestAction: "verify_in_official_app",
    conceptKeys: ["verification_habit", "authority_impersonation"],
    explainer: "Fake invoices push you to call a 'support' number. Always verify by logging into the real service yourself — never use contact info from the suspicious email.",
  },

  // 8. Fake School Portal Login
  {
    id: "fake-school-portal",
    type: "browser",
    source: "Browser",
    title: "Student portal sign-in required",
    startingContext: "A link in a message takes you to what looks like your school's portal.",
    scene: {
      from: "registrar@schoolnet-portal.app",
      subject: "Action required: re-verify your student account",
      body: "Your student account requires re-verification before Friday or your transcripts will be locked. Sign in here to confirm your credentials.",
      displayedUrl: "schoolnet-portal.app/signin",
    },
    hotspots: [
      { id: "url", phrase: "schoolnet-portal.app/signin", region: "url", label: "URL mismatch", explanation: "This isn't your school's real .edu domain.", severity: "high", concept: "verification_habit" },
      { id: "creds", phrase: "Sign in here to confirm your credentials", region: "body", label: "Credential request", explanation: "Schools don't ask you to confirm credentials via emailed links.", severity: "high" },
      { id: "deadline", phrase: "before Friday or your transcripts will be locked", region: "body", label: "Urgency manipulation", explanation: "Fake deadlines stop you from checking the URL.", severity: "medium", concept: "urgency_manipulation" },
    ],
    actions: [
      { id: "enter_password", label: "Enter your school password", tone: "danger" },
      { id: "verify_in_official_app", label: "Type the real school URL yourself", tone: "safe" },
      { id: "ask_parent", label: "Ask a parent/teacher", tone: "safe" },
      { id: "close_tab", label: "Close the tab", tone: "neutral" },
      { id: "report_phishing", label: "Report to your school's IT", tone: "neutral" },
    ],
    consequences: {
      enter_password: { moneyDelta: 0, identityRiskDelta: 55, composureDelta: -18, triggersRecovery: true, message: "Your school login was captured. The attacker tried to reset your password on file." },
      verify_in_official_app: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "Typed your real school's .edu URL — no alerts. Confirmed phishing." },
      ask_parent: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 5, message: "Smart — your parent confirmed the school never asks like this." },
      close_tab: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 1, message: "Closed. Safer than entering anything." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Reported. IT can warn other students." },
    },
    bestAction: "verify_in_official_app",
    conceptKeys: ["verification_habit", "urgency_manipulation"],
    explainer: "Never sign in through a link in a message. Open a new tab and type the school's real URL yourself.",
  },

  // 9. Fake Family Emergency Text
  {
    id: "fake-family-emergency",
    type: "text",
    source: "Texts",
    title: "Family emergency — need cash now",
    startingContext: "A text from an unknown number claims to be a family member in trouble.",
    scene: {
      from: "+1 (901) 555-0143",
      body: "It's me, I lost my phone and I'm using a friend's. I'm stuck and can't call right now. Please send $200 via Cash App to $help-mom-now. I'll explain everything later.",
      timestamp: "1 min ago",
    },
    hotspots: [
      { id: "unknown", phrase: "+1 (901) 555-0143", region: "sender", label: "Unknown number", explanation: "Treat unknown-number 'family' texts as suspicious by default.", severity: "high" },
      { id: "cant-call", phrase: "can't call right now", region: "body", label: "Refusal to call", explanation: "Scammers refuse voice contact because their voice won't match.", severity: "high" },
      { id: "cashapp", phrase: "Cash App to $help-mom-now", region: "body", label: "Instant-payment ask", explanation: "Cash App transfers are instant and largely unrecoverable.", severity: "high" },
    ],
    actions: [
      { id: "send_money", label: "Send the $200", tone: "danger" },
      { id: "call_official_number", label: "Call your family member's known number", tone: "safe" },
      { id: "ask_parent", label: "Ask another trusted adult", tone: "safe" },
      { id: "ignore", label: "Don't reply", tone: "neutral" },
      { id: "report_phishing", label: "Report and block", tone: "neutral" },
    ],
    consequences: {
      send_money: { moneyDelta: -200, account: "spending", identityRiskDelta: 0, composureDelta: -20, message: "You sent $200 to a stranger's Cash App. Funds gone instantly." },
      call_official_number: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 5, message: "You called their real number — they're safe at home. Confirmed scam." },
      ask_parent: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "A second adult confirmed it's not real. Calm save." },
      ignore: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 1, message: "Ignored — but a quick call to verify is even better." },
      report_phishing: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Reported. Carrier can flag the pattern." },
    },
    bestAction: "call_official_number",
    conceptKeys: ["urgency_manipulation", "verification_habit"],
    explainer: "Always verify 'family emergencies' by calling the known number directly. Refusal to talk on the phone is the scam tell.",
  },

  // 10. Fake Online Store Checkout
  {
    id: "fake-online-store",
    type: "browser",
    source: "Browser",
    title: "Limited drop — $19 sneakers ending soon",
    startingContext: "An ad takes you to a store with prices that look too good to be true.",
    scene: {
      body: "AIR ULTRA RUNNERS — normally $189, today only $19. Free shipping. 47 in stock. Sale ends in 02:14. Enter card details to checkout.",
      displayedUrl: "air-ultra-clearance.shop/checkout",
      productName: "Air Ultra Runner",
      amount: 19,
      rating: "★★★★★ (3 reviews)",
    },
    hotspots: [
      { id: "url", phrase: "air-ultra-clearance.shop/checkout", region: "url", label: "Strange URL", explanation: "Brand-impersonating .shop subdomains are a classic fake-store tell.", severity: "high", concept: "verification_habit" },
      { id: "price", phrase: "today only $19", region: "body", label: "Unrealistic discount", explanation: "90%+ off flagship product = scam bait.", severity: "high", concept: "too_good_to_be_true" },
      { id: "timer", phrase: "Sale ends in 02:14", region: "body", label: "Countdown pressure", explanation: "Fake countdown timers force impulse buys.", severity: "medium", concept: "urgency_manipulation" },
      { id: "reviews", phrase: "★★★★★ (3 reviews)", region: "meta", label: "Thin social proof", explanation: "Only 3 reviews on a 'limited drop' is suspicious.", severity: "low" },
    ],
    actions: [
      { id: "share_card", label: "Enter card details and check out", tone: "danger" },
      { id: "search_company", label: "Search the store name independently", tone: "safe" },
      { id: "close_tab", label: "Close the tab", tone: "safe" },
      { id: "cancel_transaction", label: "Cancel and shop with a trusted retailer", tone: "safe" },
    ],
    consequences: {
      share_card: { moneyDelta: -19, account: "checking", identityRiskDelta: 40, composureDelta: -15, triggersRecovery: true, message: "Card details captured. The $19 charge cleared and a follow-up $180 charge appeared the next day before you froze the card." },
      search_company: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 4, message: "Reviews everywhere call it a scam store. Confirmed." },
      close_tab: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 2, message: "Closed. Safe." },
      cancel_transaction: { moneyDelta: 0, identityRiskDelta: 0, composureDelta: 3, message: "Skipped and bought from the brand's real site. Smart." },
    },
    bestAction: "search_company",
    conceptKeys: ["too_good_to_be_true", "verification_habit", "urgency_manipulation"],
    explainer: "Verify any store by searching the exact URL and brand name. Real flagship products are almost never 90% off.",
  },
];

export const SCENARIO_POOL_IDS = SCAM_SCENARIOS.map((s) => s.id);

/** Pick N random scenarios with no repeats. Always seeds the bank phishing case first. */
export function pickScenarioMix(count = 4): ScamScenario[] {
  const first = SCAM_SCENARIOS.find((s) => s.id === "fake-bank-login-alert")!;
  const rest = SCAM_SCENARIOS.filter((s) => s.id !== first.id);
  for (let i = rest.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [rest[i], rest[j]] = [rest[j], rest[i]];
  }
  return [first, ...rest.slice(0, Math.max(0, count - 1))];
}

/** Display-only chips drawn from the frozen Phase 6 dictionary. */
export const FROZEN_DICTIONARY_CONCEPTS: Record<string, string> = {
  urgency_manipulation: "Urgency manipulation",
  authority_impersonation: "Authority impersonation",
  verification_habit: "Independent verification",
  too_good_to_be_true: "Unrealistic returns",
  otp_protection: "OTP / code protection",
};